import { useCallback, useMemo, useState } from 'react';

import type { ColumnDefinition } from '@/features/table/components/ColumnPicker';
import type { ColDef } from 'ag-grid-community';

export interface UseColumnVisibilityProps<T> {
  columnDefs: ColDef<T>[];
}

export interface UseColumnVisibilityResult<T> {
  visibleColumnDefs: ColDef<T>[];
  allColumns: ColumnDefinition[];
  visibleColumnIds: string[];
  handleVisibilityChange: (visibleIds: string[]) => void;
}

const getColumnId = <T>(colDef: ColDef<T>): string => {
  if (colDef.colId) return colDef.colId;
  if (colDef.field) return colDef.field as string;
  // Use headerName as fallback ID for columns with only valueGetter
  if (typeof colDef.headerName === 'string') {
    return colDef.headerName.toLowerCase().replace(/\s+/g, '-');
  }
  return '';
};

const getColumnLabel = <T>(colDef: ColDef<T>): string => {
  if (typeof colDef.headerName === 'string') {
    return colDef.headerName;
  }
  return colDef.field ?? colDef.colId ?? 'Unknown';
};

export const useColumnVisibility = <T>({ columnDefs }: UseColumnVisibilityProps<T>): UseColumnVisibilityResult<T> => {
  const allColumns = useMemo<ColumnDefinition[]>(() => {
    return columnDefs
      .filter((col) => getColumnId(col))
      .map((col) => {
        const id = getColumnId(col);
        const isActionsColumn = id === 'actions' || col.pinned === 'right';
        return {
          id,
          label: getColumnLabel(col),
          defaultVisible: col.hide !== true,
          canHide: !isActionsColumn
        };
      });
  }, [columnDefs]);

  const defaultVisibleIds = useMemo(() => {
    return allColumns.filter((col) => col.defaultVisible !== false).map((col) => col.id);
  }, [allColumns]);

  const [visibleColumnIds, setVisibleColumnIds] = useState<string[]>(defaultVisibleIds);

  const handleVisibilityChange = useCallback((visibleIds: string[]) => {
    setVisibleColumnIds(visibleIds);
  }, []);

  const visibleColumnDefs = useMemo<ColDef<T>[]>(() => {
    return columnDefs.map((col) => {
      const id = getColumnId(col);
      if (!id) return col;

      const columnDef = allColumns.find((c) => c.id === id);
      if (columnDef?.canHide === false) {
        return { ...col, hide: false };
      }

      const isVisible = visibleColumnIds.includes(id);
      return { ...col, hide: !isVisible };
    });
  }, [columnDefs, visibleColumnIds, allColumns]);

  return {
    visibleColumnDefs,
    allColumns,
    visibleColumnIds,
    handleVisibilityChange
  };
};
